// Specialist sign-in: an emailed link, no password. The link's token sits after
// the # so it never reaches a server, and it is only spent when the person
// presses the button, so a mail scanner opening the link can't use it up.
// public/index.html keeps every analytics tag off this page and the dashboard.
import React, { useEffect, useState } from "react";
import SEO from "../components/SEO";
import { finishLogin, loadDashboard, sendLoginLink } from "../components/specialistApi";
import "./specialists.css";

const DASHBOARD = "/specialists/dashboard";

export function readToken(hash = window.location.hash) {
  const match = /^#token=([A-Za-z0-9_-]{20,200})$/.exec(String(hash || ""));
  return match ? match[1] : "";
}

export default function SpecialistLogin({ go = (path) => window.location.assign(path) }) {
  const [token, setToken] = useState(readToken);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [sent, setSent] = useState(false);

  useEffect(() => {
    // Off the address bar, out of history, as soon as it has been read.
    if (window.location.hash) window.history.replaceState(null, "", window.location.pathname);
    if (token) return undefined;
    let live = true;
    loadDashboard().then((out) => {
      if (live && out.ok) go(DASHBOARD);
    });
    return () => {
      live = false;
    };
    // Only on arrival: a sign-in from this page goes on to the dashboard by itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finish = async () => {
    setBusy(true);
    setStatus("");
    const out = await finishLogin(token);
    setBusy(false);
    if (out.ok) {
      go(DASHBOARD);
      return;
    }
    setToken("");
    setStatus(
      out.status === 0
        ? "Couldn't reach BossDrop. Check your connection and try again."
        : "That link has run out or was already used. Enter your email for a new one.",
    );
  };

  const request = async (e) => {
    e.preventDefault();
    setBusy(true);
    setStatus("");
    const out = await sendLoginLink(email.trim());
    setBusy(false);
    if (out.ok) {
      setSent(true);
      return;
    }
    setStatus(
      out.status === 429
        ? "That's a lot of tries for one hour. Please wait a while and try again."
        : "Couldn't reach BossDrop. Check your connection and try again.",
    );
  };

  return (
    <section className="page container specialistPortal">
      <SEO
        title="BossDrop | Specialist log in"
        description="Sign in to your BossDrop Implementation Specialist dashboard."
        canonical="https://bossdrop.ai/specialists/login"
        url="https://bossdrop.ai/specialists/login"
        robots="noindex,nofollow"
      />
      <div className="hero">
        <h1 className="h1">Specialist log in</h1>
        {token ? null : <p className="sub">We'll email you a link. There's no password to remember.</p>}
      </div>

      <div className="panel specialistLogin">
        {token ? (
          <>
            <p style={{ marginTop: 0 }}>Press the button to finish signing in on this device.</p>
            <div className="ctaRow">
              <button className="btn btnCream" type="button" onClick={finish} disabled={busy}>
                {busy ? "Signing in..." : "Go to my dashboard"}
              </button>
            </div>
          </>
        ) : sent ? (
          <p style={{ margin: 0 }} role="status">
            If that address belongs to a specialist, a sign-in link is on its way. It works for 30 minutes. Check spam
            if it doesn't show up.
          </p>
        ) : (
          <form className="formGrid" onSubmit={request}>
            <div className="field">
              <label htmlFor="specialist-login-email">Your email</label>
              <input
                id="specialist-login-email"
                type="email"
                required
                maxLength={200}
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
              />
            </div>
            <div className="ctaRow">
              <button className="btn btnCream" type="submit" disabled={busy}>
                {busy ? "Sending..." : "Email me a link"}
              </button>
            </div>
          </form>
        )}
        <p className="formStatus" role="alert">
          {status}
        </p>
        {token ? null : (
          <p className="muted specialistLoginFoot">
            Not a specialist yet? <a href="/specialists#apply">Apply here.</a>
          </p>
        )}
      </div>
    </section>
  );
}
